import { isDespia } from "./isDespia";
import { Capacitor } from "@capacitor/core";

/**
 * Déclenche un retour haptique sur Despia (iOS/Android natif)
 * Fallback sur Capacitor Haptics si disponible
 * Ne fait rien sur web
 * 
 * Types disponibles selon la doc Despia:
 * - lighthaptic:// - Light vibration
 * - heavyhaptic:// - Heavy vibration
 * - successhaptic:// - Success vibration
 * - warninghaptic:// - Warning vibration
 * - errorhaptic:// - Error vibration
 */
async function triggerHaptic(command: string, intensity: "light" | "medium" | "heavy" = "medium"): Promise<void> {
  // Try Despia first
  if (isDespia()) {
    try {
      const { default: despia } = await import("despia-native");
      despia(command);
      return;
    } catch (error) {
      console.debug("Despia haptic not available:", error);
    }
  }

  // Fallback to Capacitor Haptics on native
  if (Capacitor.isNativePlatform()) {
    try {
      const { Haptics, ImpactStyle, NotificationType } = await import("@capacitor/haptics");
      
      if (command.includes("light")) {
        await Haptics.impact({ style: ImpactStyle.Light });
      } else if (command.includes("heavy")) {
        await Haptics.impact({ style: ImpactStyle.Heavy });
      } else if (command.includes("success")) {
        await Haptics.notification({ type: NotificationType.Success });
      } else if (command.includes("warning")) {
        await Haptics.notification({ type: NotificationType.Warning });
      } else if (command.includes("error")) {
        await Haptics.notification({ type: NotificationType.Error });
      } else {
        await Haptics.impact({ style: ImpactStyle.Medium });
      }
    } catch (error) {
      // Haptics plugin not installed, silently fail
      console.debug("Capacitor haptics not available:", error);
    }
  }
}

/**
 * Haptic léger - pour les sélections, toggles, navigation
 */
export const hapticLight = () => triggerHaptic("lighthaptic://", "light");

/**
 * Haptic moyen - pour les boutons standard
 */
export const hapticMedium = () => triggerHaptic("lighthaptic://", "medium");

/**
 * Haptic fort - pour les actions importantes
 */
export const hapticHeavy = () => triggerHaptic("heavyhaptic://", "heavy");

/**
 * Haptic succès - pour les confirmations
 */
export const hapticSuccess = () => triggerHaptic("successhaptic://");

/**
 * Haptic warning - pour les alertes
 */
export const hapticWarning = () => triggerHaptic("warninghaptic://");

/**
 * Haptic erreur - pour les erreurs
 */
export const hapticError = () => triggerHaptic("errorhaptic://");

/**
 * Haptic sélection - pour les changements de tab, toggle
 */
export const hapticSelection = () => triggerHaptic("lighthaptic://", "light");
